import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import type { HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';

import type { SavedSearchAreaBounds } from '@/src/lib/savedSearches';

/**
 * What a saved search is created from, and matched against: the area the route
 * resolved plus the slug it resolved to.
 */
export type SaveSearchTarget = {
  resourceId: string;
  legacyAreaId?: string;
  /** @deprecated Real legacy Area id only. */
  areaId?: string;
  searchSlug: string;
  /** Default name for a new saved search: the route's H1, falling back to the area's city/state. */
  defaultName: string;
  areaBounds: SavedSearchAreaBounds;
  /** The area's typed Zillow id; matches saved searches by typed id and feeds the create payload. */
  searchScope?: HpSearchScopeDto;
};

export const saveSearchTargetKey = ['search', 'saveSearchTarget'] as const;

/**
 * The Save button lives in the sticky search bar, which the `(search)` shell
 * renders above the page. A route cannot hand its resolved area up to an
 * ancestor through props, so it writes the target into the shared cache
 * instead — the seam `useCurrentArea` and `useCurrentResolvedSearch` use.
 *
 * Null until a route seeds it, which is how the HDP leaves the button inert.
 */
export const useSaveSearchTarget = () => {
  return useQuery<SaveSearchTarget | null>({
    queryKey: saveSearchTargetKey,
    queryFn: () => {
      throw new Error('Save search target should be set via useSetSaveSearchTarget');
    },
    enabled: false,
    staleTime: Infinity,
  });
};

export const useSetSaveSearchTarget = () => {
  const queryClient = useQueryClient();

  // Stable so a route's seeding effect runs on a real change, not every render.
  return useCallback(
    (target: SaveSearchTarget | null) => {
      queryClient.setQueryData(saveSearchTargetKey, target ?? null);
    },
    [queryClient],
  );
};
